/* tslint:disable */
/**
 * IE *requires* the `filter` to be a Function otherwise it throws an Error.
 * https://msdn.microsoft.com/en-us/library/ie/ff975301(v=vs.85).aspx
 *
 * Whereas MDN documents that NodeFilter *should* be an Object with an
 * `acceptNode` function, but that Firefox accepts a direct Function as well.
 * https://developer.mozilla.org/en-US/docs/Web/API/Document/createNodeIterator
 */

import { isLeft, tryCatch } from 'fp-ts/lib/Either';
import normalize from 'range-normalize';
import { ValueOf } from 'ts-essentials';

function RangeIterator(
  range: Range,
  whatToShow: ValueOf<typeof NodeFilter>,
  filter: (node: Node) => boolean,
  doc = window.document
) {
  if (!range) {
    throw new TypeError('a Range instance must be given');
  }

  // allow the `filter` to be the 2nd parameter
  if ('function' === typeof whatToShow) {
    filter = whatToShow;
    whatToShow = NodeFilter.SHOW_ALL;
  }

  let r = normalize(range.cloneRange());
  let startContainer = r.startContainer;
  let endContainer = r.endContainer;
  let start = r.commonAncestorContainer;
  r = null;

  const acceptNode: NodeFilter = {
    acceptNode: (node: Node) => {
      if (!withinRange(node)) {
        return NodeFilter.FILTER_REJECT;
      }
      if (!filter) {
        return NodeFilter.FILTER_ACCEPT;
      }
      return filter(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  };

  function withinRange(node: any) {
    if (node === startContainer || node === endContainer) {
      return true;
    }
    let s = node.compareDocumentPosition(startContainer);
    let e = node.compareDocumentPosition(endContainer);
    return (
      (s & Node.DOCUMENT_POSITION_PRECEDING ||
        s & Node.DOCUMENT_POSITION_CONTAINS) &&
      (e & Node.DOCUMENT_POSITION_FOLLOWING ||
        e & Node.DOCUMENT_POSITION_CONTAINS)
    );
  }

  return doc.createNodeIterator(start, whatToShow, acceptNode);
}

export const getTextRectsForRange = ({
  topOffset = 0,
  leftOffset = 0,
  scale = 1,
  range = undefined as any,
  document = window.document,
}) => {
  let output = [];
  const adjustRect = ({ top, left, right, bottom, width, height }: any) => ({
    top: (top - topOffset) / scale,
    left: (left - leftOffset) / scale,
    right: (right - leftOffset) / scale,
    bottom: (bottom - topOffset) / scale,
    width: width / scale,
    height: height / scale,
  });

  let iterator = RangeIterator(
    range,
    NodeFilter.SHOW_ALL,
    (node) => node.nodeType === Node.TEXT_NODE
  );

  let node;
  while ((node = iterator.nextNode())) {
    const rangePart = document.createRange();
    rangePart.selectNode(node);
    if (node === range.startContainer) {
      rangePart.setStart(node, range.startOffset);
      if (node === range.endContainer) {
        rangePart.setEnd(node, range.endOffset);
      }
    } else if (node === range.endContainer) {
      rangePart.setEnd(node, range.endOffset);
    }
    output.push(adjustRect(rangePart.getBoundingClientRect()));
  }
  return output;
};

export const getRangeTextContent = (range: Range) => {
  const result = tryCatch(
    () => range.cloneContents(),
    () => ''
  );
  if (isLeft(result)) {
    return '';
  } else {
    const contents = result.right;
    // add a space between a punctuation character (".", ";", ":", ",")
    // and the following character if there isnt one.
    // Mostly happens going from paragraph to paragraph. Added other punctuation in just in case.
    return contents.textContent
      ? contents.textContent.replace(/(.)([\.,\;,\:,\,])(.)/g, '$1$2 $3')
      : '';
  }
};

var crc32 = (function () {
  function utf8encode(str) {
    var utf8CharCodes = [];

    for (var i = 0, len = str.length, c; i < len; ++i) {
      c = str.charCodeAt(i);
      if (c < 128) {
        utf8CharCodes.push(c);
      } else if (c < 2048) {
        utf8CharCodes.push((c >> 6) | 192, (c & 63) | 128);
      } else {
        utf8CharCodes.push(
          (c >> 12) | 224,
          ((c >> 6) & 63) | 128,
          (c & 63) | 128
        );
      }
    }
    return utf8CharCodes;
  }

  var cachedCrcTable = null;

  function buildCRCTable() {
    var table = [];
    for (var i = 0, j, crc; i < 256; ++i) {
      crc = i;
      j = 8;
      while (j--) {
        if ((crc & 1) == 1) {
          crc = (crc >>> 1) ^ 0xedb88320;
        } else {
          crc >>>= 1;
        }
      }
      table[i] = crc >>> 0;
    }
    return table;
  }

  function getCrcTable() {
    if (!cachedCrcTable) {
      cachedCrcTable = buildCRCTable();
    }
    return cachedCrcTable;
  }

  return function (str) {
    var utf8CharCodes = utf8encode(str),
      crc = -1,
      crcTable = getCrcTable();
    for (var i = 0, len = utf8CharCodes.length, y; i < len; ++i) {
      y = (crc ^ utf8CharCodes[i]) & 0xff;
      crc = (crc >>> 8) ^ crcTable[y];
    }
    return (crc ^ -1) >>> 0;
  };
})();
function escapeTextForHtml(str) {
  return str.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function nodeToInfoString(node, infoParts = []) {
  infoParts = infoParts || [];
  var nodeType = node.nodeType,
    children = node.childNodes,
    childCount = children.length;
  var nodeInfo = [nodeType, node.nodeName, childCount].join(':');
  var start = '',
    end = '';
  switch (nodeType) {
    case 3: // Text node
      start = escapeTextForHtml(node.nodeValue);
      break;
    case 8: // Comment
      start = '<!--' + escapeTextForHtml(node.nodeValue) + '-->';
      break;
    default:
      start = '<' + nodeInfo + '>';
      end = '</>';
      break;
  }
  if (start) {
    infoParts.push(start);
  }
  for (var i = 0; i < childCount; ++i) {
    nodeToInfoString(children[i], infoParts);
  }
  if (end) {
    infoParts.push(end);
  }
  return infoParts;
}
//serialize/deserialize Selection range object.

function getNodeIndex(node) {
  var i = 0;
  while ((node = node.previousSibling)) {
    ++i;
  }
  return i;
}

function serializePosition(node, offset, rootNode) {
  var pathParts = [],
    n = node;
  rootNode = rootNode || node.ownerDocument.documentElement;

  while (n && !(n as Node).isEqualNode(rootNode)) {
    pathParts.push(getNodeIndex(n));

    n = n.parentNode;
  }

  return pathParts.join('/') + ':' + offset;
}

function isAncestorOf(ancestor, descendant, selfIsAncestor) {
  var n = selfIsAncestor ? descendant : descendant.parentNode;
  while (n) {
    if (n === ancestor) {
      return true;
    } else {
      n = n.parentNode;
    }
  }
  return false;
}
function getElementChecksum(el) {
  var info = nodeToInfoString(el).join('');
  return crc32(info).toString(16);
}
export function serializeRange(range, omitChecksum, rootNode) {
  if (!isAncestorOf(rootNode, range.commonAncestorContainer, true)) {
    console.warn(
      'serializeRange(): range ' +
        range.toString() +
        ' is not wholly contained within specified root node ',
      rootNode
    );
  }
  var serialized =
    serializePosition(range.startContainer, range.startOffset, rootNode) +
    ',' +
    serializePosition(range.endContainer, range.endOffset, rootNode);
  if (!omitChecksum) {
    serialized += '{' + getElementChecksum(rootNode) + '}';
  }
  return serialized;
}

function DomPosition(node, offset) {
  node = node;
  offset = offset;
}

DomPosition.prototype = {
  equals: function (pos) {
    return !!pos && this.node === pos.node && this.offset == pos.offset;
  },

  inspect: function () {
    return '[DomPosition(' + this.node + ':' + this.offset + ')]';
  },

  toString: function () {
    return this.inspect();
  },
};

export function canDeserializeRange(serialized, rootNode, doc) {
  if (!rootNode) {
    rootNode = (doc || document).documentElement;
  }
  var result = deserializeRegex.exec(serialized);
  var checksum = result && result[3];
  return !checksum || checksum === getElementChecksum(rootNode);
}

function deserializePosition(serialized, rootNode, doc) {
  if (!rootNode) {
    rootNode = (doc || document).documentElement;
  }
  var parts = serialized.split(':');
  var node = rootNode;
  var nodeIndices = parts[0] ? parts[0].split('/') : [],
    i = nodeIndices.length,
    nodeIndex;

  while (i--) {
    nodeIndex = parseInt(nodeIndices[i], 10);

    if (nodeIndex < node.childNodes.length) {
      node = node.childNodes[nodeIndex];
    } else {
      console.warn(
        'deserializePosition() failed: node ' +
          node +
          ' has no child with index ' +
          nodeIndex +
          ', ' +
          i
      );
    }
  }
  return { node: node, offset: parseInt(parts[1], 10) };
}

var deserializeRegex = /^([^,]+),([^,\{]+)(\{([^}]+)\})?$/;

export function deserializeRange(serialized, rootNode, doc) {
  if (rootNode) {
    doc = doc || (rootNode as Node).ownerDocument;
  } else {
    doc = doc || document;
    rootNode = doc.documentElement;
  }

  var result = deserializeRegex.exec(serialized);
  var checksum = result ? result[4] : undefined;
  if (checksum) {
    var rootNodeChecksum = getElementChecksum(rootNode);
    if (checksum !== rootNodeChecksum) {
      console.warn(
        'deserializeRange(): checksums of serialized range root node (' +
          checksum +
          ') and target root node (' +
          rootNodeChecksum +
          ') do not match'
      );
    }
  }

  var start = result
      ? deserializePosition(result[1], rootNode, doc)
      : undefined,
    end = result ? deserializePosition(result[2], rootNode, doc) : undefined;
  var range = result ? doc.createRange() : undefined;
  if (result) {
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
  }
  return range;
}
